import { Component, computed, inject, signal } from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import { FormsModule } from '@angular/forms';
import { filter } from 'rxjs/operators';
import { NotifiableError } from '@bugsnag/js';
import { BugsnagService } from '@cumlaude/bugsnag';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { BaseDialogComponent, DialogHeaderComponent } from '@cumlaude/shared-components-dialogs';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { FEEDBACK_TAG, FeedbackService } from '../../../services/feedback.service';

/**
 * Hoe de dialog gesloten is: 'verzonden' (feedback is verstuurd), 'later' (Vraag me later) of
 * 'gesloten' (kruisje of Escape zonder te versturen).
 */
export type NavigatieFeedbackResultaat = 'verzonden' | 'later' | 'gesloten';

interface Reden {
	id: string;
	label: string;
}

const ANDERS: Reden = { id: 'anders', label: 'Anders' };

const REDENEN: Reden[] = [
	{ id: 'dashboards-terugvinden', label: 'Ik kon dashboards lastiger terugvinden' },
	{ id: 'overzicht-kwijt', label: 'Ik raakte het overzicht kwijt' },
	{ id: 'kwam-niet-uit', label: 'Het kwam mij even niet uit' },
	ANDERS,
];

const TITEL = 'Feedback: terug naar de oude navigatie';

@Component({
	selector: 'app-navigatie-feedback-dialog',
	templateUrl: './navigatie-feedback-dialog.component.html',
	styleUrls: ['./navigatie-feedback-dialog.component.scss'],
	imports: [BaseDialogComponent, DialogHeaderComponent, ButtonComponent, FormsModule],
})
export class NavigatieFeedbackDialogComponent {
	private readonly feedbackService = inject(FeedbackService);
	private readonly toastr = inject(ToastrService);
	private readonly bugsnag = inject(BugsnagService);
	private readonly dialogRef = inject<DialogRef<NavigatieFeedbackResultaat>>(DialogRef);

	protected readonly redenen = REDENEN;

	protected readonly reden = signal<Reden | undefined>(undefined);
	protected readonly toelichting = signal('');
	protected readonly done = signal(false);
	protected readonly isBusySending = signal(false);

	protected readonly toelichtingVerplicht = computed(() => this.reden() === ANDERS);
	protected readonly kanVersturen = computed(
		() => !this.isBusySending() && this.reden() !== undefined && (!this.toelichtingVerplicht() || this.toelichting().trim().length > 0)
	);

	constructor() {
		this.dialogRef.keydownEvents.pipe(filter((event) => event.key === 'Escape')).subscribe(() => this.sluiten());
	}

	async send() {
		const reden = this.reden();
		if (!reden || !this.kanVersturen()) return;
		this.isBusySending.set(true);

		try {
			const toelichting = this.toelichting().trim();
			await this.feedbackService.post({
				title: TITEL,
				extraTags: [FEEDBACK_TAG.NAVIGATIE],
				outro: `Reden:\n${reden.label}` + (toelichting.length > 0 ? `\n\nToelichting:\n${toelichting}` : ''),
			});
			this.done.set(true);
		} catch (err) {
			this.bugsnag.notify(<NotifiableError>err);
			this.toastr.error('Er ging iets fout bij het versturen van je feedback.');
		} finally {
			this.isBusySending.set(false);
		}
	}

	later() {
		this.dialogRef.close('later');
	}

	sluiten() {
		this.dialogRef.close(this.done() ? 'verzonden' : 'gesloten');
	}
}
